import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const CheckIcon = ({ size = 10, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 10 8" fill="none" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0, ...style }}>
    <path d="M1 4L3.8 7L9 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function ProjectMultiSelect({ value = [], options, onChange, disabled, isDirty }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top:0, left:0, width:0 });
  const btnRef   = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  function toggle() {
    if (disabled) return;
    if (!open) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) });
    }
    setOpen(o => !o);
  }

  function toggleProject(id) {
    if (value.includes(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  }

  let label;
  if (value.length === 0) label = '— none —';
  else if (value.length === 1) label = options.find(o => o.id === value[0])?.name ?? '1 project';
  else label = `${value.length} projects`;

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={disabled}
        style={{
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:6,
          fontFamily:'var(--font-sans)', fontSize:12,
          padding:'5px 10px', borderRadius:'var(--radius-md)',
          border:`1px solid ${isDirty ? 'var(--color-amber)' : 'var(--color-border2)'}`,
          background:'var(--color-card2)',
          color: isDirty ? 'var(--color-amber)' : 'var(--color-text)',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          minWidth:130, maxWidth:190, outline:'none',
          transition:'border-color 0.15s, box-shadow 0.15s',
          boxShadow: open ? '0 0 0 2px var(--color-accent-border)' : 'none',
        }}
      >
        <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, textAlign:'left'}}>
          {label}
        </span>
        <span style={{transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.15s', color:'var(--color-muted2)', display:'flex'}}>
          <ChevronDown />
        </span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position:'fixed', top:pos.top, left:pos.left, minWidth:pos.width,
            background:'rgba(255,255,255,0.95)',
            backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
            border:'1px solid rgba(199,210,254,0.6)', borderRadius:'var(--radius-md)',
            boxShadow:'0 8px 32px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
            zIndex:9999, overflow:'hidden', maxHeight:220, overflowY:'auto',
          }}
        >
          {/* Clear all */}
          <div
            onClick={() => onChange([])}
            style={{
              padding:'7px 12px', fontSize:11, cursor:'pointer',
              color: value.length === 0 ? '#94A3B8' : '#64748B',
              borderBottom:'1px solid rgba(199,210,254,0.4)',
              background: value.length === 0 ? '#F8FAFC' : 'transparent',
              fontStyle:'italic',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F1F5F9'; }}
            onMouseLeave={e => { e.currentTarget.style.background = value.length === 0 ? '#F8FAFC' : 'transparent'; }}
          >
            — none —
          </div>
          {options.map(opt => {
            const checked = value.includes(opt.id);
            return (
              <div
                key={opt.id}
                onClick={() => toggleProject(opt.id)}
                style={{
                  padding:'8px 12px', fontSize:12, cursor:'pointer',
                  color: checked ? '#4338CA' : '#0F172A',
                  background: checked ? '#EEF2FF' : 'transparent',
                  display:'flex', alignItems:'center', gap:9,
                  transition:'background 0.1s',
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#F1F5F9'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width:15, height:15, borderRadius:4, flexShrink:0,
                  border:`1.5px solid ${checked ? '#4338CA' : '#CBD5E1'}`,
                  background: checked ? '#4338CA' : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'background 0.1s, border-color 0.1s',
                }}>
                  {checked && (
                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                      <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{opt.name}</span>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function DeleteModal({ session, onConfirm, onCancel, deleting }) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return createPortal(
    <div
      style={{
        position:'fixed', inset:0, zIndex:10000,
        background:'rgba(3,10,60,0.7)',
        backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:24,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background:'rgba(255,255,255,0.96)',
          backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
          border:'1px solid rgba(226,232,240,0.8)',
          borderRadius:'var(--radius-xl)',
          boxShadow:'0 24px 64px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,1)',
          padding:'28px 28px 24px',
          width:'100%', maxWidth:400,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Icon */}
        <div style={{
          width:44, height:44, borderRadius:'50%',
          background:'rgba(229,62,62,0.12)', border:'1px solid rgba(229,62,62,0.25)',
          display:'flex', alignItems:'center', justifyContent:'center',
          marginBottom:18,
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8.5 3h3a1 1 0 0 1 1 1v1h3.5a.5.5 0 0 1 0 1H4a.5.5 0 0 1 0-1H7.5V4a1 1 0 0 1 1-1ZM5.5 7l.9 9.1A1 1 0 0 0 7.4 17h5.2a1 1 0 0 0 1-.9L14.5 7h-9Z" fill="rgba(229,62,62,0.9)"/>
          </svg>
        </div>

        <h2 style={{
          fontFamily:'var(--font-display)', fontSize:17, fontWeight:700,
          color:'#0F172A', margin:'0 0 8px', letterSpacing:'-0.01em',
        }}>
          Delete session?
        </h2>
        <p style={{fontSize:14, color:'#475569', lineHeight:1.6, margin:'0 0 6px'}}>
          <span style={{color:'#0F172A', fontWeight:600}}>"{session.name}"</span> will be permanently deleted.
        </p>
        <p style={{fontSize:13, color:'#94A3B8', margin:'0 0 24px'}}>
          This cannot be undone.
        </p>

        <div style={{display:'flex', gap:10}}>
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{
              flex:1, padding:'10px 0', borderRadius:'var(--radius-lg)',
              background:'transparent', border:'1px solid #E2E8F0',
              color:'#64748B', fontSize:14, fontFamily:'var(--font-display)',
              fontWeight:600, cursor:'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              flex:1, padding:'10px 0', borderRadius:'var(--radius-lg)',
              background:'var(--color-danger)', border:'none',
              color:'#fff', fontSize:14, fontFamily:'var(--font-display)',
              fontWeight:700, cursor: deleting ? 'default' : 'pointer',
              opacity: deleting ? 0.7 : 1,
            }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Pagination({ page, total, pageSize, onChange }) {
  const totalPages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  // Build page number list with ellipsis: always show first, last, and ±2 around current
  function pageNumbers() {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '…') {
        pages.push('…');
      }
    }
    return pages;
  }

  const btn = (content, onClick, active = false, disabled = false) => (
    <button
      key={content}
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 32, height: 32, padding: '0 8px',
        borderRadius: 'var(--radius-md)',
        border: active ? '1px solid var(--color-accent-border)' : '1px solid var(--color-border)',
        background: active ? 'var(--color-accent-dim)' : 'transparent',
        color: active ? 'var(--color-accent)' : disabled ? 'var(--color-muted2)' : 'var(--color-muted)',
        fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: active ? 700 : 500,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'background 0.1s, border-color 0.1s',
      }}
    >
      {content}
    </button>
  );

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 32, flexWrap:'wrap', gap:12 }}>
      <span style={{ fontSize:13, color:'var(--color-muted)' }}>
        Showing <strong style={{color:'var(--color-text)'}}>{from}–{to}</strong> of <strong style={{color:'var(--color-text)'}}>{total}</strong> sessions
      </span>
      <div style={{ display:'flex', gap:4 }}>
        {btn('←', () => onChange(page - 1), false, page === 1)}
        {pageNumbers().map((n, i) =>
          n === '…'
            ? <span key={`ellipsis-${i}`} style={{display:'flex', alignItems:'center', padding:'0 4px', color:'var(--color-muted2)', fontSize:13}}>…</span>
            : btn(n, () => onChange(n), n === page)
        )}
        {btn('→', () => onChange(page + 1), false, page === totalPages)}
      </div>
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';
const STATUS_COLOR = { ready: 'var(--color-green)', processing: 'var(--color-amber)', failed: 'var(--color-danger)', queued: 'var(--color-muted)', uploading: 'var(--color-accent)' };
const STATUS_BG    = { ready: 'rgba(42,139,122,0.12)', processing: 'rgba(245,169,127,0.12)', failed: 'rgba(229,62,62,0.10)', queued: 'rgba(0,0,0,0.05)', uploading: 'rgba(0,255,255,0.08)' };
const ACCEPTED = '.mp3,.mp4,.wav,.m4a,.txt,.pdf,.docx';
const POLL_INTERVAL = 5000;
const MAX_POLLS = 72; // 6 minutes
const PAGE_SIZE = 20;

export default function Sessions() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending]   = useState({});   // session_id -> project_ids string[] (unsaved)
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [page, setPage]   = useState(1);
  const [total, setTotal] = useState(0);
  const pollsRef  = useRef({});
  const fileInput = useRef(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const offset = (page - 1) * PAGE_SIZE;
      const [sessRes, projRes] = await Promise.all([
        fetch(`${API_BASE}/v1/sessions?limit=${PAGE_SIZE}&offset=${offset}`, { headers }),
        fetch(`${API_BASE}/v1/projects`, { headers }),
      ]);
      if (sessRes.ok) {
        const d = await sessRes.json();
        setSessions(d.sessions || []);
        setTotal(d.total || 0);
        (d.sessions || []).forEach(s => {
          if (s.status === 'queued' || s.status === 'processing') startPolling(s.id);
        });
      }
      if (projRes.ok) setProjects(await projRes.json());
    } catch {
      setError('Could not load sessions.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, page]);

  useEffect(() => {
    setPending({});
    loadData();
    return () => Object.values(pollsRef.current).forEach(p => clearInterval(p.interval));
  }, [loadData]);

  // ── Polling ───────────────────────────────────────────────────────────────
  function startPolling(sessionId) {
    if (pollsRef.current[sessionId]) return; // already polling
    let count = 0;
    const interval = setInterval(async () => {
      count++;
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/v1/sessions/${sessionId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { status, progress_pct } = await res.json();
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, status, progress_pct } : s));
        if (status === 'ready' || status === 'failed' || count >= MAX_POLLS) {
          clearInterval(pollsRef.current[sessionId]?.interval);
          delete pollsRef.current[sessionId];
        }
      } catch { /* network hiccup — keep polling */ }
    }, POLL_INTERVAL);
    pollsRef.current[sessionId] = { interval, count };
  }

  // ── Delete session ────────────────────────────────────────────────────────
  async function confirmAndDelete() {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setDeleting(true);
    if (pollsRef.current[id]) {
      clearInterval(pollsRef.current[id].interval);
      delete pollsRef.current[id];
    }
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/v1/sessions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(prev => {
        const next = prev.filter(s => s.id !== id);
        // If we emptied the page and it's not page 1, go back
        if (next.length === 0 && page > 1) setPage(p => p - 1);
        return next;
      });
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  }

  // ── Upload ────────────────────────────────────────────────────────────────
  async function uploadFiles(files) {
    const fileList = Array.from(files);
    if (!fileList.length) return;

    const token = await getToken();

    // Add placeholder rows immediately
    const placeholders = fileList.map(f => ({
      id: `uploading-${f.name}-${Date.now()}`,
      name: f.name.replace(/\.[^/.]+$/, ''),
      status: 'uploading',
      quote_count: 0,
      created_at: new Date().toISOString(),
      project: null,
      _placeholder: true,
    }));
    setSessions(prev => [...placeholders, ...prev]);

    // Upload each file in parallel
    await Promise.all(fileList.map(async (file, i) => {
      const placeholderId = placeholders[i].id;
      const sessionName = file.name.replace(/\.[^/.]+$/, '');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_name', sessionName);
      try {
        const res = await fetch(`${API_BASE}/v1/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { session_id } = await res.json();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'session_upload', file_type: file.name.split('.').pop().toLowerCase() });
        // Replace placeholder with real session row
        setSessions(prev => prev.map(s => s.id === placeholderId ? {
          ...s,
          id: session_id,
          status: 'queued',
          _placeholder: false,
        } : s));
        startPolling(session_id);
      } catch {
        setSessions(prev => prev.map(s => s.id === placeholderId ? { ...s, status: 'failed' } : s));
      }
    }));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  }

  function handleFileInput(e) {
    uploadFiles(e.target.files);
    e.target.value = '';
  }

  // ── Project assignment ────────────────────────────────────────────────────
  function handleProjectChange(sessionId, newProjectIds) {
    const original = JSON.stringify([...(sessions.find(s => s.id === sessionId)?.projects || [])].sort());
    const updated  = JSON.stringify([...newProjectIds].sort());
    if (original === updated) {
      // Back to original — remove from pending
      setPending(prev => { const n = { ...prev }; delete n[sessionId]; return n; });
    } else {
      setPending(prev => ({ ...prev, [sessionId]: newProjectIds }));
    }
  }

  const dirtyCount = Object.keys(pending).length;

  async function saveChanges() {
    if (!dirtyCount) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const results = await Promise.all(
        Object.entries(pending).map(([sessionId, projectIds]) =>
          fetch(`${API_BASE}/v1/sessions/${sessionId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_ids: projectIds }),
          })
        )
      );
      if (!results.every(r => r.ok)) throw new Error('Some updates failed');
      setSessions(prev => prev.map(s =>
        pending.hasOwnProperty(s.id) ? { ...s, projects: pending[s.id], project: pending[s.id][0] || null } : s
      ));
      setPending({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Failed to save some changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function discardChanges() { setPending({}); }

  if (loading) return <div style={s.center}>Loading sessions...</div>;

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {dragging && (
        <div style={s.dragOverlay}>
          <div style={s.dragMsg}>Drop files to upload as new sessions</div>
        </div>
      )}

      {/* Header */}
      <div style={s.header} className="scq-page-header">
        <div>
          <h1 style={s.h1}>Sessions</h1>
          <p style={s.subtitle}>{total > 0 ? `${total} session${total !== 1 ? 's' : ''} total` : 'All research sessions across your account'}</p>
        </div>
        <div style={s.headerActions} className="scq-header-actions">
          {saved && <span style={s.savedBadge}><CheckIcon size={11} style={{marginRight:5}} /> Saved</span>}
          {dirtyCount > 0 && (
            <>
              <button style={s.discardBtn} onClick={discardChanges} disabled={saving}>Discard</button>
              <button style={s.saveBtn} onClick={saveChanges} disabled={saving}>
                {saving ? 'Saving…' : `Save ${dirtyCount} change${dirtyCount !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          <button style={s.uploadBtn} onClick={() => fileInput.current.click()}>
            ⬆ Upload sessions
          </button>
        </div>
      </div>

      {error && <p style={s.errorMsg}>{error}</p>}

      {/* Drop zone hint (when empty) */}
      {sessions.length === 0 && (
        <div style={s.dropZone} onClick={() => fileInput.current.click()}>
          <div style={s.dropIcon}>⬆</div>
          <div style={s.dropTitle}>Drop files here or click to upload</div>
          <div style={s.dropSub}>mp3, mp4, wav, m4a, txt, pdf, docx · multiple files supported</div>
        </div>
      )}

      {/* Table */}
      {sessions.length > 0 && (
        <div className="scq-card" style={s.card}>
          <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
          <table style={{...s.table, minWidth:560}}>
            <thead>
              <tr>
                {['Name', 'Status', 'Progress', 'Project', 'Quotes', 'Date', ''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(sess => {
                const isUploading = sess.status === 'uploading';
                const isLive      = sess.status === 'queued' || sess.status === 'processing';
                const currentProjects = pending.hasOwnProperty(sess.id) ? pending[sess.id] : (sess.projects || []);
                const isDirty = pending.hasOwnProperty(sess.id);
                return (
                  <tr key={sess.id} style={{ ...s.tr, ...(isDirty ? s.trDirty : {}) }}>
                    <td style={s.td}>
                      <div style={s.sessionName}>{sess.name}</div>
                      {!sess._placeholder && (
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
                          <div style={s.sessionId}>{sess.id.slice(0, 8)}…</div>
                          {sess.status === 'ready' && (
                            <button
                              style={s.sessionChatBtn}
                              onClick={() => navigate('/chat', { state: { sessionId: sess.id, sessionName: sess.name } })}
                            >
                              ✦ Chat
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, background: STATUS_BG[sess.status] || STATUS_BG.queued, color: STATUS_COLOR[sess.status] || 'var(--color-muted)' }}>
                        {isLive ? <span style={s.pulse} /> : null}
                        {sess.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      {(isUploading || isLive) ? (
                        <div style={s.progressTrack}>
                          <div style={{ ...s.progressFill, width: isUploading ? '15%' : sess.status === 'processing' ? '60%' : '10%' }} />
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: sess.status === 'ready' ? 'var(--color-green)' : 'var(--color-muted2)' }}>
                          {sess.status === 'ready' ? <><CheckIcon size={10} style={{marginRight:4}} />Done</> : sess.status === 'failed' ? '✕ Failed' : '—'}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      <ProjectMultiSelect
                        value={currentProjects}
                        options={projects}
                        onChange={val => handleProjectChange(sess.id, val)}
                        disabled={isUploading || sess._placeholder}
                        isDirty={isDirty}
                      />
                    </td>
                    <td style={s.td}>{sess.quote_count || 0}</td>
                    <td style={s.td}>{sess.created_at ? new Date(sess.created_at).toLocaleDateString() : '—'}</td>
                    <td style={{...s.td, textAlign:'right'}}>
                      {!sess._placeholder && (
                        <button style={s.deleteBtn} onClick={() => setConfirmDelete(sess)} title="Delete session">✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Drop hint inside table when sessions exist */}
          <div style={s.tableDropHint} onClick={() => fileInput.current.click()}>
            ⬆ Drop more files here or click to add sessions
          </div>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      )}

      {/* Sticky save bar */}
      {dirtyCount > 0 && (
        <div style={s.stickyBar}>
          <span style={s.stickyMsg}>{dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.discardBtn} onClick={discardChanges} disabled={saving}>Discard</button>
            <button style={s.saveBtn} onClick={saveChanges} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <DeleteModal
          session={confirmDelete}
          onConfirm={confirmAndDelete}
          onCancel={() => setConfirmDelete(null)}
          deleting={deleting}
        />
      )}

      <style>{`
        @keyframes scqPulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes progressShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

const s = {
  center:      { color: 'var(--color-muted)', textAlign: 'center', padding: '80px 0', fontSize: 14 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  h1:          { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--color-text)', margin: 0 },
  subtitle:    { color: 'var(--color-muted)', marginTop: 6, fontSize: 14 },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  uploadBtn:   { padding: '10px 18px', background: 'var(--color-magma)', color: '#fff', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  saveBtn:     { padding: '10px 20px', background: 'var(--color-card2)', color: 'var(--color-accent)', border: '1px solid var(--color-border2)', borderRadius: 'var(--radius-lg)', fontSize: 13, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer' },
  discardBtn:  { padding: '10px 16px', background: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', fontSize: 13, cursor: 'pointer' },
  savedBadge:  { fontSize: 13, color: 'var(--color-green)', fontWeight: 600 },
  errorMsg:    { color: 'var(--color-danger)', fontSize: 13, marginBottom: 16 },

  dropZone:    { border: '2px dashed var(--color-border2)', borderRadius: 'var(--radius-xl)', padding: '60px 24px', textAlign: 'center', cursor: 'pointer', marginBottom: 24, transition: 'border-color 0.2s' },
  dropIcon:    { fontSize: 32, marginBottom: 12, color: 'var(--color-muted2)' },
  dropTitle:   { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 },
  dropSub:     { fontSize: 13, color: 'var(--color-muted2)' },

  dragOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,255,255,0.06)', border: '3px dashed var(--color-accent)', borderRadius: 'var(--radius-xl)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' },
  dragMsg:     { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--color-accent)' },

  card:          { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: 24, marginBottom: 80, boxShadow: 'var(--shadow-card)' },
  table:         { width: '100%', borderCollapse: 'collapse' },
  th:            { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--color-muted2)', borderBottom: '1px solid var(--color-border)', textTransform: 'uppercase', letterSpacing: '0.07em' },
  tr:            { borderBottom: '1px solid var(--color-border)' },
  trDirty:       { background: 'rgba(255,87,34,0.04)' },
  td:            { padding: '13px 12px', fontSize: 14, color: 'var(--color-text)', verticalAlign: 'middle' },
  sessionName:     { fontWeight: 500 },
  sessionId:       { fontSize: 11, fontFamily: 'monospace', color: 'var(--color-muted2)' },
  sessionChatBtn:  { padding: '2px 8px', background: 'var(--color-accent-dim)', color: 'var(--color-accent)', border: '1px solid var(--color-accent-border)', borderRadius: 'var(--radius-sm)', fontSize: 10, fontFamily: 'var(--font-display)', fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.01em' },
  statusBadge:   { padding: '3px 9px', borderRadius: 100, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 },
  pulse:         { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: 'scqPulse 1.2s infinite' },
  progressTrack: { height: 5, background: 'var(--color-border)', borderRadius: 3, overflow: 'hidden', width: '100%', minWidth: 60 },
  progressFill:  { height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, var(--color-accent) 0%, var(--color-accent-dim) 50%, var(--color-accent) 100%)', backgroundSize: '200% 100%', animation: 'progressShimmer 1.8s linear infinite' },
  tableDropHint: { marginTop: 16, paddingTop: 14, borderTop: '1px dashed var(--color-border)', textAlign: 'center', fontSize: 12, color: 'var(--color-muted2)', cursor: 'pointer' },

  stickyBar:   { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-card)', border: '1px solid var(--color-border2)', borderRadius: 'var(--radius-lg)', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 50 },
  stickyMsg:   { fontSize: 13, color: 'var(--color-muted)' },
  deleteBtn:   { background: 'none', border: 'none', color: 'var(--color-muted2)', fontSize: 14, cursor: 'pointer', padding: '2px 6px', borderRadius: 'var(--radius-sm)', lineHeight: 1, opacity: 0.6 },
};
